import React, { useState, useEffect } from 'react';
import { DistanceUnit } from '../../../types/enums';

interface UserDistanceValue {
  distance: number;
  unit: string;
}

interface DistancePickerProps {
  value: UserDistanceValue | null;
  onChange: (value: UserDistanceValue) => void;
  minKm?: number;
  maxKm?: number;
  minMiles?: number;
  maxMiles?: number;
}

const DistancePicker: React.FC<DistancePickerProps> = ({
  value,
  onChange,
  minKm = 1,
  maxKm = 50,
  minMiles = 1,
  maxMiles = 30,
}) => {
  const [unit, setUnit] = useState<string>(value?.unit || DistanceUnit.KM);
  const currentMin = unit === DistanceUnit.KM ? minKm : minMiles;
  const currentMax = unit === DistanceUnit.KM ? maxKm : maxMiles;
  const currentDistance = value?.distance ?? currentMin;

  // Emit default value on mount so Continue is enabled immediately
  useEffect(() => {
    if (!value) {
      onChange({ distance: minKm, unit: DistanceUnit.KM });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnitChange = (newUnit: string) => {
    setUnit(newUnit);
    const newMin = newUnit === DistanceUnit.KM ? minKm : minMiles;
    const newMax = newUnit === DistanceUnit.KM ? maxKm : maxMiles;
    const clamped = Math.max(newMin, Math.min(newMax, currentDistance));
    onChange({ distance: clamped, unit: newUnit });
  };

  const handleDistanceChange = (delta: number) => {
    const step = currentMax > 100 ? 5 : 1;
    const newVal = Math.max(currentMin, Math.min(currentMax, currentDistance + delta * step));
    onChange({ distance: newVal, unit });
  };

  const btnStyle: React.CSSProperties = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(250,253,251,0.08)',
    border: '1px solid rgba(250,253,251,0.16)',
    color: 'var(--color-text-light)',
    fontSize: 24,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    fontFamily: "'Figtree', sans-serif",
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
      {/* Unit toggle */}
      <div style={{
        display: 'flex',
        background: 'rgba(250,253,251,0.08)',
        borderRadius: 12,
        border: '1px solid rgba(250,253,251,0.16)',
        overflow: 'hidden',
      }}>
        {[DistanceUnit.KM, DistanceUnit.MILE].map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => handleUnitChange(u)}
            style={{
              flex: 1,
              padding: '12px 32px',
              background: unit === u ? 'var(--color-primary)' : 'transparent',
              border: 'none',
              color: unit === u ? 'var(--color-text-primary)' : 'rgba(250,253,251,0.5)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: 16,
              fontWeight: 400,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {u === DistanceUnit.KM ? 'KM' : 'Miles'}
          </button>
        ))}
      </div>

      {/* Number control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        <button
          type="button"
          style={{ ...btnStyle, opacity: currentDistance <= currentMin ? 0.3 : 1 }}
          onClick={() => handleDistanceChange(-1)}
          disabled={currentDistance <= currentMin}
        >
          −
        </button>
        <span style={{
          fontFamily: "'Figtree', sans-serif",
          fontSize: 48,
          fontWeight: 400,
          color: 'var(--color-text-light)',
          minWidth: 100,
          textAlign: 'center',
        }}>
          {currentDistance}
        </span>
        <button
          type="button"
          style={{ ...btnStyle, opacity: currentDistance >= currentMax ? 0.3 : 1 }}
          onClick={() => handleDistanceChange(1)}
          disabled={currentDistance >= currentMax}
        >
          +
        </button>
      </div>
      <span style={{
        fontFamily: "'Figtree', sans-serif",
        fontSize: 16,
        color: 'rgba(250,253,251,0.5)',
      }}>
        {unit === DistanceUnit.KM ? 'km' : 'miles'}
      </span>
    </div>
  );
};

export default DistancePicker;
